// src/components/ChatbotDialog.js
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  List,
  ListItem,
  Paper,
  Avatar,
  Typography,
  Tooltip,
  Button,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import DownloadIcon from "@mui/icons-material/Download";
import ReactMarkdown from "react-markdown";
import * as XLSX from "xlsx";
import "./TypingIndicator.css";

/* ================== CẤU HÌNH GỌI BACKEND ================== */
// Gọi trực tiếp Cloud Run – KHÔNG dùng /api
const ENDPOINT = (process.env.REACT_APP_CF_NLQUERY_ENDPOINT || "").trim();
const USE_AUTH =
  String(process.env.REACT_APP_USE_AUTH || "false").toLowerCase() === "true";

/* ================== LỊCH SỬ (tùy chọn) ================== */
const HISTORY_KEY = "kho-bot-history-v2";
const PERSIST_HISTORY = false;
const MAX_TURNS_STORE = 50;
const MAX_TURNS_SEND = 6;
const MAX_CHARS_PER_TURN = 200;

const welcomeMessage =
  "Xin chào! Tôi là **Kho-Bot** (AI trợ lý kho). Hãy hỏi: _“Tổng doanh thu tháng 9/2025”_, _“Đơn liên quan đến CÔNG TY A”_, _“SKU 204”_…";

/* ================== HELPERS ================== */
function loadHistory() {
  if (!PERSIST_HISTORY) return [];
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function saveHistory(arr) {
  if (!PERSIST_HISTORY) return;
  try {
    localStorage.setItem(
      HISTORY_KEY,
      JSON.stringify(arr.slice(-MAX_TURNS_STORE))
    );
  } catch {}
}
function clearHistory() {
  try {
    localStorage.removeItem(HISTORY_KEY);
  } catch {}
}
function compressHistory(arr) {
  const last = arr.slice(-MAX_TURNS_SEND).map((x) => ({
    role: x.sender === "user" ? "user" : "bot",
    text: (x.text || "").toString().slice(0, MAX_CHARS_PER_TURN),
  }));
  return last;
}
const formatNumber = (n) =>
  typeof n === "number"
    ? n.toLocaleString("vi-VN", { maximumFractionDigits: 2 })
    : n;

/* ================== MARKDOWN ĐẸP ================== */
const EnhancedContent = ({ children }) => {
  const content =
    typeof children === "string" ? children : children?.toString() || "";
  return (
    <Typography
      component="div"
      sx={{
        "& p": { m: 0, lineHeight: 1.7, fontSize: "1rem" },
        "& ol, & ul": { m: 0, pl: 2.5, my: 1 },
        "& li": { mb: 0.5 },
        "& strong": { fontWeight: 700, color: "#667eea" },
        "& code": {
          bgcolor: "rgba(102,126,234,0.1)",
          p: "2px 6px",
          borderRadius: "6px",
          fontSize: "0.9em",
          fontFamily: "monospace",
        },
      }}
    >
      <ReactMarkdown>{content}</ReactMarkdown>
    </Typography>
  );
};

/* ================== HIỂN THỊ DÒNG DỮ LIỆU + XUẤT EXCEL ================== */
const RowsDisplay = ({ title, rows }) => {
  const [downloading, setDownloading] = useState(false);

  // Ánh xạ key -> nhãn tiếng Việt
  const keyMap = {
    id: "Mã",
    date: "Ngày",
    partner: "Đối tác",
    warehouse: "Kho",
    amount: "Số tiền",
    type: "Loại",
    sku: "SKU",
    name: "Tên",
    qty: "SL",
    revenue: "Doanh thu",
    unitPrice: "Đơn giá",
    "Tồn (Nhập−Xuất)": "Tồn (Nhập−Xuất)",
    "Tồn hiện tại (products.quantity)": "Tồn hiện tại",
  };

  const toViRow = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
      const keyVi = keyMap[k] || k;
      out[keyVi] = typeof v === "number" ? formatNumber(v) : v;
    });
    return out;
  };

  const handleExport = () => {
    try {
      setDownloading(true);
      const data = (rows || []).map(toViRow);
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "DuLieu");
      XLSX.writeFile(wb, `kho-bot-${Date.now()}.xlsx`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        p: 2.5,
        borderRadius: "12px",
        bgcolor: "#fff",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        border: "1px solid #e5e7eb",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 1.5,
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "#1f2937",
            fontSize: "1.05rem",
            borderBottom: "2px solid #667eea",
            pb: 0.5,
            display: "inline-block",
          }}
        >
          {title || "Kết quả"}
        </Typography>

        <Tooltip title="Xuất Excel (.xlsx)">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={downloading || !rows?.length}
              onClick={handleExport}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                borderColor: "#667eea",
                color: "#4f46e5",
                "&:hover": { borderColor: "#4f46e5", bgcolor: "rgba(79,70,229,.06)" },
              }}
            >
              Xuất Excel
            </Button>
          </span>
        </Tooltip>
      </Box>

      {(rows || []).slice(0, 100).map((r, idx) => (
        <Box
          key={idx}
          sx={{
            borderTop: idx === 0 ? "none" : "1px dashed #e5e7eb",
            pt: idx === 0 ? 0 : 1,
            mt: idx === 0 ? 0 : 1,
          }}
        >
          {Object.entries(toViRow(r)).map(([k, v]) => (
            <Box key={k} sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "#667eea",
                  mr: 1.5,
                  mt: "9px",
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                <Box component="span" sx={{ fontWeight: 600, color: "#374151" }}>
                  {k}:
                </Box>{" "}
                <Box component="span" sx={{ color: "#4b5563" }}>
                  {typeof v === "object" ? JSON.stringify(v) : `${v ?? ""}`}
                </Box>
              </Typography>
            </Box>
          ))}
        </Box>
      ))}

      {!rows?.length && (
        <Typography variant="body2" sx={{ color: "#6b7280", mt: 1 }}>
          Không có dữ liệu.
        </Typography>
      )}
    </Box>
  );
};

/* ================== GÕ ĐANG NHẬP ================== */
const TypingIndicator = () => (
  <ListItem
    sx={{
      alignItems: "flex-start",
      gap: 2,
      mb: 2,
      opacity: 0,
      animation: "fadeInUp 0.5s ease-out forwards",
    }}
  >
    <Avatar
      sx={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        width: 45,
        height: 45,
      }}
    >
      <HeadsetMicIcon sx={{ color: "white", fontSize: "1.3rem" }} />
    </Avatar>
    <Box
      className="chat-content"
      sx={{
        bgcolor: "#fff",
        p: 2.5,
        borderRadius: "20px 20px 20px 6px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        border: "1px solid rgba(102,126,234,0.1)",
        minWidth: "80px",
      }}
    >
      <div className="typing-indicator">
        <span style={{ backgroundColor: "#667eea" }}></span>
        <span style={{ backgroundColor: "#667eea" }}></span>
        <span style={{ backgroundColor: "#667eea" }}></span>
      </div>
    </Box>
  </ListItem>
);

/* ================== SPEECH-TO-TEXT (tùy chọn) ================== */
function createRecognizer({ lang, onText, onStart, onEnd, onError }) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const rec = new SR();
  rec.lang = lang || navigator.language || "vi-VN";
  rec.interimResults = true;
  rec.continuous = false;
  rec.maxAlternatives = 1;

  rec.onstart = () => onStart && onStart();
  rec.onerror = (e) => onError && onError(e);
  rec.onend = () => onEnd && onEnd();
  rec.onresult = (ev) => {
    let finalText = "";
    for (let i = ev.resultIndex; i < ev.results.length; i++) {
      const t = ev.results[i][0].transcript;
      if (ev.results[i].isFinal) finalText += t;
    }
    if (finalText.trim()) onText && onText(finalText.trim());
  };
  return rec;
}

/* ================== COMPONENT ================== */
export default function ChatbotDialog({ open, onClose }) {
  const [messages, setMessages] = useState([{ sender: "bot", text: welcomeMessage }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Khi mở/đóng
  useEffect(() => {
    if (open) {
      const init = loadHistory();
      setMessages(init.length ? init : [{ sender: "bot", text: welcomeMessage }]);
      setInput("");
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      clearHistory();
    }
  }, [open]);

  // Auto scroll + lưu lịch sử
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollIntoView({ behavior: "smooth" });
    if (open) saveHistory(messages);
  }, [messages, loading, open]);

  /* =============== GỌI BACKEND (Cloud Run trực tiếp) =============== */
  async function callBackend(query, historyCompressed) {
    if (!ENDPOINT) {
      throw new Error(
        "Thiếu REACT_APP_CF_NLQUERY_ENDPOINT (URL Cloud Run). Hãy cấu hình và build lại."
      );
    }
    const url = ENDPOINT.endsWith("/nlquery") ? ENDPOINT : `${ENDPOINT}/nlquery`;

    const headers = { "Content-Type": "application/json" };
    if (USE_AUTH) {
      try {
        const { getAuth } = await import("firebase/auth");
        const idToken = await getAuth()?.currentUser?.getIdToken?.();
        if (idToken) headers["Authorization"] = `Bearer ${idToken}`;
      } catch {}
    }

    const resp = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ message: query, history: historyCompressed }),
    });

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      if (resp.status === 404 || txt.includes("Cannot POST /api/nlquery")) {
        throw new Error(
          `HTTP ${resp.status} – ${txt}\n• URL: ${url}\n• Có vẻ bạn còn trỏ vào /api. Hãy trỏ thẳng Cloud Run bằng REACT_APP_CF_NLQUERY_ENDPOINT.`
        );
      }
      if (resp.status === 0 || txt.includes("CORS")) {
        throw new Error(
          `Không gọi được API (CORS?). Hãy kiểm tra ALLOWED_ORIGINS trên backend Cloud Run.`
        );
      }
      throw new Error(`HTTP ${resp.status} – ${txt || "Lỗi không xác định"}`);
    }
    return await resp.json();
  }

  /* =============== ĐẨY MESSAGE BOT =============== */
  const pushBotMessage = (payload) => {
    // Bảng rows
    if (payload?.ok && payload?.result?.kind === "rows") {
      const rows = Array.isArray(payload.result.rows) ? payload.result.rows : [];
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: payload.reply || `Có ${rows.length} bản ghi.` },
        { sender: "bot", ui: { type: "rows", title: "Kết quả", rows } },
      ]);
      return;
    }

    // Chi tiết 1 record (tra SKU…)
    if (payload?.ui?.component === "SimpleBulletDisplay" && payload?.ui?.data) {
      setMessages((prev) => [
        ...prev,
        ...(payload.answer ? [{ sender: "bot", text: payload.answer }] : []),
        { sender: "bot", ui: { type: "rows", title: payload.ui.title || "Kết quả", rows: [payload.ui.data] } },
      ]);
      return;
    }

    // Tổng/đếm
    if (payload?.ok && ["sum", "count"].includes(payload?.result?.kind)) {
      setMessages((prev) => [...prev, { sender: "bot", text: payload.reply }]);
      return;
    }

    // Văn bản thường
    setMessages((prev) => [
      ...prev,
      { sender: "bot", text: payload?.answer || payload?.reply || "..." },
    ]);
  };

  /* =============== GỬI =============== */
  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { sender: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);

    const currentInput = input;
    setInput("");
    setLoading(true);
    setTimeout(() => inputRef.current?.focus(), 50);

    try {
      const historyCompressed = compressHistory([...messages, userMessage]);
      const data = await callBackend(currentInput, historyCompressed);
      pushBotMessage(data);
    } catch (error) {
      const msg =
        (error && (error.message || error.toString())) ||
        "Đã xảy ra sự cố không xác định.";
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text:
            "⚠️ **Lỗi kết nối:** " +
            msg +
            (ENDPOINT
              ? `\n\n• URL: ${ENDPOINT.endsWith("/nlquery") ? ENDPOINT : ENDPOINT + "/nlquery"}`
              : ""),
        },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  /* =============== MIC =============== */
  const toggleMic = () => {
    if (isListening) {
      try {
        recognitionRef.current && recognitionRef.current.stop();
      } catch {}
      return;
    }
    const rec = createRecognizer({
      lang: navigator.language || "vi-VN",
      onStart: () => setIsListening(true),
      onEnd: () => setIsListening(false),
      onError: () => setIsListening(false),
      onText: (t) => setInput((prev) => (prev ? prev + " " + t : t)),
    });
    if (!rec) {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "⚠️ Trình duyệt không hỗ trợ nhập giọng nói." },
      ]);
      return;
    }
    recognitionRef.current = rec;
    rec.start();
  };

  /* =============== ĐÓNG =============== */
  const handleClose = () => {
    clearHistory();
    setMessages([{ sender: "bot", text: welcomeMessage }]);
    setInput("");
    onClose?.();
  };

  // Cảnh báo nếu thiếu endpoint
  useEffect(() => {
    if (!ENDPOINT) {
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text:
            "⚠️ Chưa cấu hình `REACT_APP_CF_NLQUERY_ENDPOINT`. Hãy đặt URL Cloud Run rồi build lại.",
        },
      ]);
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          height: "85vh",
          borderRadius: "24px",
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          overflow: "hidden",
          boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
          "@keyframes fadeInUp": {
            from: { opacity: 0, transform: "translateY(20px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        },
      }}
      sx={{ "& .MuiDialog-container": { backdropFilter: "blur(8px)" } }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 3,
          px: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              width: 50,
              height: 50,
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            <HeadsetMicIcon sx={{ color: "white", fontSize: "1.5rem" }} />
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 0.5, textShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
            >
              Kho-Bot Assistant
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: "0.9rem", fontWeight: 500 }}>
              🤖 Trợ lý ảo thông minh cho quản lý kho
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={handleClose}
          sx={{
            color: "white",
            bgcolor: "rgba(255,255,255,0.15)",
            width: 44,
            height: 44,
            "&:hover": { bgcolor: "rgba(255,255,255,0.25)", transform: "rotate(90deg)" },
            transition: "all 0.3s ease",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 0,
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 3,
            backgroundColor: "transparent",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              background: "rgba(102,126,234,0.1)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
            },
          }}
        >
          <List sx={{ py: 0 }}>
            {messages.map((msg, index) => (
              <ListItem
                key={index}
                sx={{
                  flexDirection: msg.sender === "user" ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  gap: 2,
                  mb: 2,
                  px: 1,
                  opacity: 0,
                  animation: `fadeInUp 0.5s ease-out ${index * 0.08}s forwards`,
                }}
              >
                <Avatar
                  sx={{
                    background:
                      msg.sender === "user"
                        ? "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    width: 45,
                    height: 45,
                  }}
                >
                  {msg.sender === "user" ? (
                    <PersonIcon sx={{ color: "white", fontSize: "1.3rem" }} />
                  ) : (
                    <HeadsetMicIcon sx={{ color: "white", fontSize: "1.3rem" }} />
                  )}
                </Avatar>

                <Box
                  className="chat-content"
                  sx={{
                    background:
                      msg.sender === "user"
                        ? "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
                        : "#ffffff",
                    color: msg.sender === "user" ? "white" : "#1a202c",
                    p: 2.5,
                    borderRadius:
                      msg.sender === "user" ? "20px 20px 6px 20px" : "20px 20px 20px 6px",
                    maxWidth: "75%",
                    boxShadow:
                      msg.sender === "user"
                        ? "0 10px 30px rgba(79,70,229,0.3)"
                        : "0 10px 30px rgba(0,0,0,0.1)",
                    border: msg.sender === "user" ? "none" : "1px solid rgba(102,126,234,0.1)",
                  }}
                >
                  {msg.sender === "user" ? (
                    <Typography sx={{ lineHeight: 1.7, fontSize: "1rem" }}>
                      {msg.text}
                    </Typography>
                  ) : msg.ui?.type === "rows" ? (
                    <RowsDisplay title={msg.ui.title} rows={msg.ui.rows} />
                  ) : (
                    <EnhancedContent>{msg.text}</EnhancedContent>
                  )}
                </Box>
              </ListItem>
            ))}
            {loading && <TypingIndicator />}
            <div ref={scrollRef} />
          </List>
        </Paper>

        <Box
          sx={{
            display: "flex",
            p: 3,
            alignItems: "center",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)",
            borderTop: "1px solid rgba(102,126,234,0.1)",
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="💬 Nhập tin nhắn của bạn..."
            disabled={loading}
            inputRef={inputRef}
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#ffffff",
                borderRadius: "20px",
                fontSize: "1rem",
                border: "2px solid rgba(102,126,234,0.2)",
                transition: "all 0.3s ease",
                "&:hover": { border: "2px solid rgba(102,126,234,0.4)" },
                "&.Mui-focused": {
                  border: "2px solid #667eea",
                  boxShadow: "0 0 0 3px rgba(102,126,234,0.2)",
                },
                "& fieldset": { border: "none" },
              },
              "& .MuiInputBase-input": { py: 1.8, px: 2.5, fontSize: "1rem" },
            }}
          />

          <Tooltip title={isListening ? "Dừng ghi" : "Nói để nhập"}>
            <span>
              <IconButton
                onClick={toggleMic}
                disabled={loading}
                sx={{
                  background: isListening
                    ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                    : "linear-gradient(135deg, #10b981 0%, #22c55e 100%)",
                  color: "white",
                  width: 56,
                  height: 56,
                  borderRadius: "18px",
                  boxShadow: "0 8px 20px rgba(16,185,129,0.3)",
                }}
              >
                {isListening ? (
                  <StopCircleIcon sx={{ fontSize: "1.6rem" }} />
                ) : (
                  <MicIcon sx={{ fontSize: "1.6rem" }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <IconButton
            onClick={handleSend}
            disabled={loading || !input.trim()}
            sx={{
              background:
                loading || !input.trim()
                  ? "linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)"
                  : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              width: 56,
              height: 56,
              borderRadius: "18px",
              boxShadow: "0 8px 20px rgba(102,126,234,0.3)",
              "&:hover:not(:disabled)": {
                transform: "scale(1.05) translateY(-2px)",
              },
            }}
          >
            <SendIcon sx={{ fontSize: "1.5rem" }} />
          </IconButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
